import {
  jumpGroundedCentreY,
  jumpInputBufferBeats,
  JUMP_CEILING_FORGIVENESS_FRAC,
  JUMP_SPIKE_FORGIVENESS_FRAC,
  type JumpChart,
} from '../../jumpChart.js';
import { decodeJumpGrid, JUMP_GROUND_Y } from '../../jumpGrid.js';

/**
 * Reason a Jump run ended in failure:
 *   - `collided`: touched a spike tip, slammed a terrain leading-face, cracked
 *     the cube's top edge on a ceiling underside, or overlapped a saw.
 *   - `fell`: walked off a ledge into a pit / chasm (no floor to land on).
 */
export type JumpDeathReason = 'collided' | 'fell';

export type JumpPassVerdict = 'perfect' | 'good';

/**
 * One obstacle the cube cleared alive. Mirrors Waves' `WavesResolution` so
 * the shared close-call FX / analytics shape stays consistent across modes.
 */
export interface JumpResolution {
  obstacleId: string;
  beat: number;
  /** Vertical units the cube's bottom edge cleared the obstacle's top by. */
  clearance: number;
  verdict: JumpPassVerdict;
}

/**
 * A piecewise-constant ground segment: the cube rests on `topY` (its centre
 * at `topY - playerHalf`) for any beat in `[startBeat, endBeat)`. The terrain
 * floor profile spans the whole chart gaplessly; raised platforms poke up.
 * A `lethal` segment is a pit / chasm at the grid-bottom death-Y: "landing" on
 * it is a fall-death, not a touchdown.
 */
export interface JumpSurfaceSegment {
  startBeat: number;
  endBeat: number;
  topY: number;
  /** Pit / chasm: a landing here kills the cube (death reason `fell`). */
  lethal?: boolean;
}

/**
 * A lethal point hazard, sampled at its exact `beat`. Two collision shapes:
 *   - One-sided (default, `bottomY` unset): the cube's bottom must clear `topY`
 *     (`cubeCentreY + half <= topY`) — a ground SPIKE or a terrain BLOCK face.
 *   - Vertically bounded (`bottomY` set): the cube dies if its body OVERLAPS
 *     the region `[topY, bottomY]` — a floating SAW you arc over or duck under.
 * A spike-wall / cluster is just adjacent spike columns: each grid cell compiles
 * to its own point hazard, so one arc clears (and scores) every tooth it spans.
 */
export interface JumpHazard {
  id: string;
  beat: number;
  topY: number;
  kind: 'spike' | 'block' | 'saw';
  /** Lower edge of a vertically-bounded lethal region (saws). Unset = one-sided. */
  bottomY?: number;
}

/**
 * An overhead kill-bound spanning `[startBeat, endBeat]`: the cube dies if its
 * TOP edge rises above `bottomY` while under it (`cubeCentreY − ceilingHazardHalf
 * < bottomY`). The mirror of a {@link JumpHazard} — that one bounds the cube
 * from BELOW (clear over it), this one from ABOVE (stay under it) — so it gets
 * its own list and collision pass rather than overloading `JumpHazard`. Lethal
 * only: clearing a ceiling never scores (see {@link import('../../jumpChart.js').JumpCeiling}).
 */
export interface JumpCeilingHazard {
  id: string;
  startBeat: number;
  endBeat: number;
  bottomY: number;
}

export interface JumpEngineState {
  beat: number;
  /** True while the button is held. Drives auto-jump on each landing. */
  pressing: boolean;
  /**
   * Beat of the most recent `down` edge not yet consumed by a launch.
   * `NEGATIVE_INFINITY` once consumed (or never pressed). A landing within
   * {@link JumpEngineConfig.inputBufferBeats} of this fires a buffered jump even
   * if the button was already released — the GD/Impossible jump-buffer grace.
   */
  lastDownBeat: number;
  /** True while the cube rests on a surface (vy held at 0 by the surface). */
  grounded: boolean;
  /**
   * Beat at which the current motion segment was last anchored (an input
   * edge, landing, walk-off, or tick boundary). The cube's centre Y since
   * then is:
   *   - grounded: constant `anchorY` (the surface holds it).
   *   - airborne: `anchorY + anchorVy·(b−anchorBeat) + ½·gravity·(b−anchorBeat)²`.
   */
  anchorBeat: number;
  /** Cube centre Y at {@link anchorBeat}. */
  anchorY: number;
  /** Cube vertical velocity (units/beat, +down) at {@link anchorBeat}. 0 when grounded. */
  anchorVy: number;
  /** Cube's current centre Y, synced to the trajectory at the latest tick boundary. */
  cubeY: number;
  /**
   * Centre Y of the surface the current jump launched from (the resting centre
   * at lift-off). A release while ascending clamps the cube onto the short arc
   * whose apex is `shortJumpVelocity² / (2·gravity)` ABOVE this Y, so the short
   * jump's height is identical no matter where in the early ascent the button
   * was let go. Only meaningful while airborne.
   */
  launchCentreY: number;

  alive: boolean;
  completed: boolean;
  deathReason: JumpDeathReason | null;
  deathBeat: number | null;
  collisionObstacleId: string | null;

  score: number;
  combo: number;
  maxCombo: number;
  perfects: number;
  goods: number;
  oks: number;
  misses: number;
  /** Hazards cleared alive, so re-fed `[fromBeat, toBeat]` windows don't double-score. */
  passedHazardIds: Set<string>;
  resolutions: JumpResolution[];
}

export interface JumpEngineConfig {
  chart: JumpChart;
  gravity: number;
  /** Launch speed of a full (long) jump — a held press flies the whole arc. */
  jumpVelocity: number;
  /**
   * Launch speed of a short (tapped) jump apex (< {@link jumpVelocity}). The
   * engine never launches AT this directly for a held press; instead an early
   * release while ascending clamps the cube onto the short arc whose apex is
   * `shortJumpVelocity² / (2·gravity)` above the launch surface (see
   * {@link import('../../jumpChart.js').JumpChart.shortJumpVelocity}). A buffered
   * tap (released before touchdown) launches straight onto this short arc.
   */
  shortJumpVelocity: number;
  /** Half the cube side length — the hitbox reach from centre to edge (landing + grading). */
  playerHalf: number;
  /**
   * Reduced half-extent used for the SPIKE kill-test only (= `playerHalf · (1 −
   * {@link import('../../jumpChart.js').JUMP_SPIKE_FORGIVENESS_FRAC})`). Smaller
   * than {@link playerHalf}, so clipping a spike tip survives. Blocks keep the
   * full `playerHalf` (a solid wall stays solid).
   */
  spikeHazardHalf: number;
  /**
   * Reduced half-extent used for the CEILING kill-test (= `playerHalf · (1 −
   * {@link import('../../jumpChart.js').JUMP_CEILING_FORGIVENESS_FRAC})`). The
   * upper-edge analogue of {@link spikeHazardHalf}: grazing a low overhang on a
   * slightly-too-big jump survives.
   */
  ceilingHazardHalf: number;
  /** Jump-buffer window in beats — a `down` this close before a landing fires a buffered jump. */
  inputBufferBeats: number;
  /** Flat baseline ground top (the default terrain height) — a render/HUD reference. */
  groundY: number;
  /** Piecewise-constant terrain surface (floor tops + lethal pits), sorted, gapless over the chart. */
  surface: JumpSurfaceSegment[];
  /** Lethal point hazards (spike tips, saw regions, terrain leading-faces), sorted by beat ascending. */
  hazards: JumpHazard[];
  /** Overhead kill-bounds (ceilings), sorted by start beat ascending. */
  ceilings: JumpCeilingHazard[];
}

/**
 * Compile the grid's terrain profile into the gapless piecewise-constant
 * surface. Pit columns become `lethal` segments at the grid-bottom death-Y, so
 * the cube can walk off a ledge, fall, and "land" fatally — reusing the normal
 * landing solver. The decoder already merges equal-height runs.
 */
export function buildJumpSurface(chart: JumpChart): JumpSurfaceSegment[] {
  const { terrain } = decodeJumpGrid(chart.grid);
  if (terrain.length === 0) {
    return [{ startBeat: 0, endBeat: Math.max(chart.totalBeats, 1), topY: JUMP_GROUND_Y }];
  }
  return terrain.map((span) => {
    const seg: JumpSurfaceSegment = { startBeat: span.startBeat, endBeat: span.endBeat, topY: span.topY };
    if (span.pit) {
      seg.lethal = true;
    }
    return seg;
  });
}

/**
 * Derive the lethal point-hazard list — ground spikes, floating saws, and
 * terrain leading-faces — sorted by beat. Spikes/faces are one-sided (clear the
 * top); saws carry a `bottomY` so the engine kills on region overlap instead.
 */
export function buildJumpHazards(chart: JumpChart): JumpHazard[] {
  const decoded = decodeJumpGrid(chart.grid);
  const hazards: JumpHazard[] = [];
  for (const spike of decoded.spikes) {
    hazards.push({ id: spike.id, beat: spike.beat, topY: spike.tipY, kind: 'spike' });
  }
  for (const saw of decoded.saws) {
    hazards.push({ id: saw.id, beat: saw.beat, topY: saw.topY, kind: 'saw', bottomY: saw.bottomY });
  }
  for (const face of decoded.blockFaces) {
    hazards.push({ id: face.id, beat: face.beat, topY: face.topY, kind: 'block' });
  }
  hazards.sort((a, b) => a.beat - b.beat);
  return hazards;
}

/** Derive the overhead kill-bound (ceiling) list, sorted by start beat. */
export function buildJumpCeilings(chart: JumpChart): JumpCeilingHazard[] {
  const { overheads } = decodeJumpGrid(chart.grid);
  const ceilings: JumpCeilingHazard[] = overheads.map((ov) => ({
    id: ov.id,
    startBeat: ov.startBeat,
    endBeat: ov.endBeat,
    bottomY: ov.bottomY,
  }));
  ceilings.sort((a, b) => a.startBeat - b.startBeat);
  return ceilings;
}

export function buildJumpEngineConfig(chart: JumpChart): JumpEngineConfig {
  const playerHalf = chart.playerSize / 2;
  return {
    chart,
    gravity: chart.gravity,
    jumpVelocity: chart.jumpVelocity,
    shortJumpVelocity: chart.shortJumpVelocity,
    playerHalf,
    spikeHazardHalf: playerHalf * (1 - JUMP_SPIKE_FORGIVENESS_FRAC),
    ceilingHazardHalf: playerHalf * (1 - JUMP_CEILING_FORGIVENESS_FRAC),
    inputBufferBeats: jumpInputBufferBeats(chart.bpm, chart.gravity, chart.jumpVelocity),
    groundY: JUMP_GROUND_Y,
    surface: buildJumpSurface(chart),
    hazards: buildJumpHazards(chart),
    ceilings: buildJumpCeilings(chart),
  };
}

export function createInitialJumpState(chart: JumpChart): JumpEngineState {
  const restY = jumpGroundedCentreY(chart);
  return {
    beat: 0,
    pressing: false,
    lastDownBeat: Number.NEGATIVE_INFINITY,
    grounded: true,
    anchorBeat: 0,
    anchorY: restY,
    anchorVy: 0,
    cubeY: restY,
    launchCentreY: restY,
    alive: true,
    completed: false,
    deathReason: null,
    deathBeat: null,
    collisionObstacleId: null,
    score: 0,
    combo: 0,
    maxCombo: 0,
    perfects: 0,
    goods: 0,
    oks: 0,
    misses: 0,
    passedHazardIds: new Set(),
    resolutions: [],
  };
}

/**
 * Reset chart-relative parts of a Jump state for the next infinite-mode loop.
 * Score / combo / maxCombo / perfects / goods accumulate across loops;
 * everything else returns to a grounded spawn. Mutates in place.
 */
export function resetJumpStateForNextLoop(state: JumpEngineState, chart: JumpChart): void {
  const restY = jumpGroundedCentreY(chart);
  state.beat = 0;
  state.pressing = false;
  state.lastDownBeat = Number.NEGATIVE_INFINITY;
  state.grounded = true;
  state.anchorBeat = 0;
  state.anchorY = restY;
  state.anchorVy = 0;
  state.cubeY = restY;
  state.launchCentreY = restY;
  state.alive = true;
  state.completed = false;
  state.deathReason = null;
  state.deathBeat = null;
  state.collisionObstacleId = null;
  state.passedHazardIds = new Set();
  state.resolutions = [];
  // score, combo, maxCombo, perfects, goods intentionally preserved.
}

/**
 * Surface segment covering `beat` (left-continuous: at an exact boundary the
 * segment STARTING there wins, except `totalBeats` which uses the last). The
 * cube resting here sits at `topY − playerHalf`.
 */
export function surfaceSegmentAt(
  segments: ReadonlyArray<JumpSurfaceSegment>,
  beat: number,
): JumpSurfaceSegment {
  const n = segments.length;
  if (beat <= segments[0].startBeat) {
    return segments[0];
  }
  if (beat >= segments[n - 1].endBeat) {
    return segments[n - 1];
  }
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (segments[mid].startBeat <= beat) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return segments[lo];
}

/** Top Y of the surface under `beat`. */
export function surfaceTopAt(segments: ReadonlyArray<JumpSurfaceSegment>, beat: number): number {
  return surfaceSegmentAt(segments, beat).topY;
}
